import { useMemo, useState } from 'react'
import { useUsers } from '../hooks/useUsers'

// MultiAssigneePicker — assign an action to several people (item 17). Selected
// people show as removable chips; below them a search box filters a scrollable
// checklist so you can tick as many people as you like in one go (no more
// open-dropdown-pick-repeat). Bound to dbo.Users.id via useUsers (shared 5-min
// cache). An empty array is the "unassigned" state and round-trips cleanly as
// `assigneeIds: []`.

interface Props {
  value: number[]
  onChange: (ids: number[]) => void
  label?: string
  disabled?: boolean
}

export function MultiAssigneePicker({ value, onChange, label = 'Assignees', disabled }: Props) {
  const { data: users, isLoading, error } = useUsers()
  const [query, setQuery] = useState('')

  const byId = useMemo(() => new Map((users ?? []).map((u) => [u.id, u])), [users])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    const list = users ?? []
    if (!q) return list
    return list.filter((u) => (u.name ?? '').toLowerCase().includes(q))
  }, [users, query])

  const toggle = (id: number) => {
    if (disabled) return
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id])
  }
  const remove = (id: number) => onChange(value.filter((v) => v !== id))
  const clearAll = () => onChange([])

  return (
    <div className="block">
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-xs uppercase tracking-wide font-semibold text-ink-soft">
          {label}{value.length > 0 && <span className="ml-1 text-green">· {value.length}</span>}
        </span>
        <div className="flex items-center gap-2">
          {isLoading && <span className="text-xs text-ink-soft font-mono">loading…</span>}
          {value.length > 0 && !disabled && (
            <button
              type="button"
              onClick={clearAll}
              className="text-xs text-ink-soft hover:text-oxblood transition-colors"
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {value.map((id) => {
            const u = byId.get(id)
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 rounded-full bg-green-soft text-green text-xs font-semibold"
              >
                {u?.name ?? `User ${id}`}
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => remove(id)}
                    aria-label={`Remove ${u?.name ?? 'assignee'}`}
                    className="w-4 h-4 rounded-full hover:bg-green/20 flex items-center justify-center leading-none"
                  >
                    ×
                  </button>
                )}
              </span>
            )
          })}
        </div>
      )}

      <input
        type="search"
        value={query}
        disabled={disabled || isLoading || !!error}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search people to add…"
        className="w-full border border-line rounded px-3 py-2 text-sm bg-white focus:outline-none focus:border-green focus:ring-1 focus:ring-green/30 transition-colors disabled:opacity-50 mb-1.5"
      />

      <div className="border border-line rounded max-h-44 overflow-y-auto divide-y divide-line">
        {filtered.length === 0 ? (
          <div className="px-3 py-3 text-xs text-ink-soft italic">
            {isLoading ? 'Loading people…' : 'No people match that search.'}
          </div>
        ) : (
          filtered.map((u) => {
            const checked = value.includes(u.id)
            return (
              <label
                key={u.id}
                className={
                  'flex items-center gap-2.5 px-3 py-1.5 text-sm cursor-pointer transition-colors ' +
                  (checked ? 'bg-green-soft/50' : 'hover:bg-parchment') +
                  (disabled ? ' opacity-50 cursor-not-allowed' : '')
                }
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => toggle(u.id)}
                  className="w-4 h-4 accent-green flex-shrink-0"
                />
                <span className="flex-1 truncate">{u.name ?? `(unnamed user #${u.id})`}</span>
                {u.avatarInitials && (
                  <span className="text-[10px] font-mono text-ink-soft flex-shrink-0">{u.avatarInitials}</span>
                )}
              </label>
            )
          })
        )}
      </div>

      {value.length === 0 && (
        <p className="text-xs text-ink-soft italic mt-1">Unassigned — tick one or more people above.</p>
      )}

      {error && (
        <div className="text-xs text-oxblood mt-1">
          Could not load users: {(error as Error).message}
        </div>
      )}
    </div>
  )
}
